import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import LoadingSpinner from "components/LoadingSpinner";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useAuthz } from "context/AuthzContext";
import { useClient } from "context/ClientContext";
import { listClientProfiles, apiRequest } from "services/tenantUserAdminService";

import {
  IoCheckmarkCircle, IoWarning, IoAlertCircle,
  IoTrendingUp, IoTrendingDown, IoRefresh, IoSparkles,
} from "react-icons/io5";
import { FiInfo } from "react-icons/fi";
import { TC } from "../../tokens/text";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CARD_BG = "linear-gradient(127.09deg, rgba(6,11,40,0.94) 19.41%, rgba(10,14,35,0.49) 76.65%)";
const CARD_BORDER = "1px solid rgba(255,255,255,0.08)";

const SEVERITY_CONFIG = {
  critical: { color: "#f44336", icon: <IoAlertCircle size={18} />,   bg: "rgba(255,71,87,0.08)",   border: "rgba(255,71,87,0.25)" },
  warning:  { color: "#F7B731", icon: <IoWarning size={18} />,        bg: "rgba(247,183,49,0.08)",  border: "rgba(247,183,49,0.25)" },
  positive: { color: "#01B574", icon: <IoTrendingUp size={18} />,     bg: "rgba(1,181,116,0.08)",   border: "rgba(1,181,116,0.25)" },
  info:     { color: "#4ECDC4", icon: <FiInfo size={18} />,           bg: "rgba(78,205,196,0.08)",  border: "rgba(78,205,196,0.25)" },
};

function InsightCard({ insight }) {
  const cfg = SEVERITY_CONFIG[insight.severity] || SEVERITY_CONFIG.info;
  return (
    <VuiBox mb={1.5} sx={{
      background: cfg.bg,
      border: `1px solid ${cfg.border}`,
      borderRadius: 2,
      p: "14px 16px",
    }}>
      <VuiBox display="flex" alignItems="flex-start" gap={1.5}>
        <span style={{ color: cfg.color, flexShrink: 0, mt: 2 }}>{cfg.icon}</span>
        <VuiBox flex={1}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0.3}>
            <VuiTypography sx={{ color: cfg.color, fontSize: 13, fontWeight: 700 }}>
              {insight.title}
            </VuiTypography>
            <span style={{
              background: `${cfg.color}22`, color: cfg.color,
              border: `1px solid ${cfg.color}44`,
              borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
            }}>
              {insight.severity}
            </span>
          </VuiBox>
          <VuiTypography sx={{ color: TC.midtone, fontSize: 13, lineHeight: 1.5 }}>
            {insight.detail}
          </VuiTypography>
          {insight.amount != null && (
            <VuiTypography sx={{ color: TC.secondary, fontSize: 12, mt: 0.5 }}>
              Amount: {new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(insight.amount)}
            </VuiTypography>
          )}
        </VuiBox>
      </VuiBox>
    </VuiBox>
  );
}

function NarrativeCard({ narrative, onApprove }) {
  const isApproved = narrative?.status === "approved";
  if (!narrative) return null;
  return (
    <VuiBox>
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <VuiBox display="flex" alignItems="center" gap={1.5}>
          <IoSparkles color="#F7B731" size={18} />
          <VuiTypography sx={{ color: TC.primary, fontSize: 14, fontWeight: 700 }}>
            AI-Generated Management Commentary
          </VuiTypography>
        </VuiBox>
        <span style={{
          background: isApproved ? "rgba(1,181,116,0.18)" : "rgba(247,183,49,0.18)",
          color: isApproved ? "#01B574" : "#F7B731",
          border: `1px solid ${isApproved ? "rgba(1,181,116,0.4)" : "rgba(247,183,49,0.4)"}`,
          borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700,
        }}>
          {isApproved ? "Approved" : "Draft"}
        </span>
      </VuiBox>

      {/* Narrative text */}
      <VuiBox sx={{
        background: "#f4f8ff", border: "1px solid #d8e6f7",
        borderRadius: 2, p: "18px 20px", mb: 2,
      }}>
        <VuiTypography sx={{ color: TC.primary, fontSize: 14, lineHeight: 1.8, whiteSpace: "pre-line" }}>
          {narrative.approved_text || narrative.narrative_text}
        </VuiTypography>
      </VuiBox>

      {/* Disclaimer */}
      <VuiBox mb={2} sx={{ background: "rgba(247,183,49,0.06)", border: "1px solid rgba(247,183,49,0.18)", borderRadius: 2, p: "10px 14px" }}>
        <VuiTypography sx={{ color: "#F7B731", fontSize: 12 }}>
          ⚠️ {narrative.disclaimer || "AI-generated draft — requires accountant review and approval before use."}
        </VuiTypography>
      </VuiBox>

      {!isApproved && (
        <VuiBox display="flex" gap={1.5}>
          <VuiButton color="success" variant="contained" size="small"
            onClick={() => onApprove(narrative.narrative_text, false)}
            sx={{ fontSize: 12, fontWeight: 700 }}>
            Approve as-is ✓
          </VuiButton>
          <VuiButton color="info" variant="outlined" size="small"
            onClick={() => onApprove(narrative.narrative_text, true)}
            sx={{ fontSize: 12 }}>
            Edit & Approve
          </VuiButton>
        </VuiBox>
      )}
      {isApproved && (
        <VuiBox display="flex" alignItems="center" gap={1}>
          <IoCheckmarkCircle color="#01B574" size={16} />
          <VuiTypography sx={{ color: "#01B574", fontSize: 13 }}>
            Approved {narrative.approved_at ? new Date(narrative.approved_at).toLocaleDateString("en-GB") : ""}
          </VuiTypography>
        </VuiBox>
      )}
    </VuiBox>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Advisory() {
  const { tenantId } = useAuthz();
  const { pinnedClientId, pinClient } = useClient() || {};

  const [profiles, setProfiles]           = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [insights, setInsights]             = useState([]);
  const [narrative, setNarrative]           = useState(null);
  const [loading, setLoading]               = useState(false);
  const [genLoading, setGenLoading]         = useState(false);
  const [narLoading, setNarLoading]         = useState(false);
  const [error, setError]                   = useState("");

  // Approve dialog state
  const [approveOpen, setApproveOpen]       = useState(false);
  const [editMode, setEditMode]             = useState(false);
  const [editedText, setEditedText]         = useState("");
  const [approving, setApproving]           = useState(false);

  const now = new Date();
  const currentPeriod = now.toISOString().slice(0, 7);
  const periodOptions = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return d.toISOString().slice(0, 7);
  });
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);

  useEffect(() => {
    if (!tenantId) return;
    listClientProfiles({ tenantId, status: "active" })
      .then(d => {
        const list = d.clientProfiles || [];
        setProfiles(list);
        const nextSelected =
          (pinnedClientId && list.some((client) => client.id === pinnedClientId) && pinnedClientId) ||
          list[0]?.id ||
          "";
        setSelectedClient(nextSelected);
        if (nextSelected && nextSelected !== pinnedClientId) {
          const match = list.find((client) => client.id === nextSelected);
          pinClient?.(nextSelected, match?.legalName || match?.tradingName || nextSelected);
        }
      }).catch(() => {});
  }, [tenantId, pinnedClientId, pinClient]);

  const loadInsights = useCallback(async () => {
    if (!tenantId || !selectedClient) return;
    setLoading(true); setError("");
    try {
      const base = `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}`;
      const [insightData, narData] = await Promise.allSettled([
        apiRequest(`${base}/insights`, { query: { period: selectedPeriod } }),
        apiRequest(`${base}/narratives`),
      ]);
      if (insightData.status === "fulfilled") {
        setInsights(Array.isArray(insightData.value?.insights) ? insightData.value.insights : []);
      }
      if (narData.status === "fulfilled") {
        const list = narData.value?.narratives || [];
        // Find narrative for current period
        const match = list.find(n => n.period_id === selectedPeriod);
        setNarrative(match || null);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [tenantId, selectedClient, selectedPeriod]);

  useEffect(() => { loadInsights(); }, [loadInsights]);

  const handleGenerateInsights = async () => {
    setGenLoading(true); setError("");
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/insights/generate`,
        { method: "POST", body: { period: selectedPeriod } }
      );
      setInsights(Array.isArray(data?.insights) ? data.insights : []);
    } catch (e) { setError(e.message); } finally { setGenLoading(false); }
  };

  const handleGenerateNarrative = async () => {
    setNarLoading(true); setError("");
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/narratives/generate`,
        { method: "POST", body: { period: selectedPeriod } }
      );
      setNarrative(data);
    } catch (e) { setError(e.message); } finally { setNarLoading(false); }
  };

  const openApprove = (text, edit) => {
    setEditedText(text);
    setEditMode(edit);
    setApproveOpen(true);
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/narratives/${selectedPeriod}/approve`,
        { method: "POST", body: { editedText: editMode ? editedText : undefined } }
      );
      setNarrative(data);
      setApproveOpen(false);
    } catch (e) { setError(e.message); } finally { setApproving(false); }
  };

  // Group insights by severity
  const bySeverity = {
    critical: insights.filter(i => i.severity === "critical"),
    warning:  insights.filter(i => i.severity === "warning"),
    positive: insights.filter(i => i.severity === "positive"),
    info:     insights.filter(i => i.severity === "info"),
  };
  const hasInsights = insights.length > 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Header */}
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary }}>🧠 Advisory & Insights</VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>
              AI-assisted analysis and management commentary — accountant-approved before use
            </VuiTypography>
          </VuiBox>
          <VuiBox display="flex" gap={2} flexWrap="wrap">
            <Select value={selectedClient} onChange={e => {
              const next = e.target.value;
              setSelectedClient(next);
              const match = profiles.find((client) => client.id === next);
              pinClient?.(next, match?.legalName || match?.tradingName || next);
            }}
              sx={{ color: TC.primary, background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: 2, minWidth: 200, "& .MuiSvgIcon-root": { color: TC.primary }, fontSize: 13 }}>
              {profiles.map(p => <MenuItem key={p.id} value={p.id}>{p.legalName || p.tradingName || p.id}</MenuItem>)}
            </Select>
            <Select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
              sx={{ color: TC.primary, background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: 2, minWidth: 130, "& .MuiSvgIcon-root": { color: TC.primary }, fontSize: 13 }}>
              {periodOptions.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
            <VuiButton size="small" color="info" variant="outlined" startIcon={<IoRefresh size={14} />}
              onClick={loadInsights} sx={{ fontSize: 12 }}>Refresh</VuiButton>
          </VuiBox>
        </VuiBox>

        {error && (
          <VuiBox mb={2} sx={{ background: "rgba(255,71,87,0.1)", border: "1px solid rgba(255,71,87,0.3)", borderRadius: 2, p: "12px 16px" }}>
            <VuiTypography sx={{ color: "#f44336", fontSize: 13 }}>{error}</VuiTypography>
          </VuiBox>
        )}

        <Grid container spacing={3}>
          {/* Insights panel */}
          <Grid item xs={12} lg={5}>
            <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
              <VuiBox p={3}>
                <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <VuiTypography sx={{ color: TC.primary, fontSize: 15, fontWeight: 700 }}>
                    Insights · {selectedPeriod}
                  </VuiTypography>
                  <VuiButton size="small" color="info" variant="outlined"
                    onClick={handleGenerateInsights} disabled={genLoading || !selectedClient}
                    sx={{ fontSize: 11 }}>
                    {genLoading ? "Analysing…" : "Re-analyse"}
                  </VuiButton>
                </VuiBox>

                {loading ? (
                  <LoadingSpinner size="sm" label="Loading…" />
                ) : !hasInsights ? (
                  <VuiBox py={2} textAlign="center">
                    <VuiTypography sx={{ color: TC.secondary, fontSize: 13, mb: 2 }}>
                      No insights for this period yet.
                    </VuiTypography>
                    <VuiButton color="info" variant="contained" size="small"
                      onClick={handleGenerateInsights} disabled={genLoading || !selectedClient}>
                      {genLoading ? "Analysing…" : "Generate Insights"}
                    </VuiButton>
                  </VuiBox>
                ) : (
                  <>
                    {/* Summary chips */}
                    <VuiBox display="flex" gap={1} flexWrap="wrap" mb={2.5}>
                      {[
                        { label: "Critical", count: bySeverity.critical.length, color: "#f44336" },
                        { label: "Warnings", count: bySeverity.warning.length,  color: "#F7B731" },
                        { label: "Positive", count: bySeverity.positive.length, color: "#01B574" },
                        { label: "Info",     count: bySeverity.info.length,     color: "#4ECDC4" },
                      ].filter(s => s.count > 0).map(({ label, count, color }) => (
                        <span key={label} style={{ background: `${color}18`, color, border: `1px solid ${color}33`, borderRadius: 5, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>
                          {count} {label}
                        </span>
                      ))}
                    </VuiBox>

                    {/* Ordered: critical → warning → positive → info */}
                    {["critical", "warning", "positive", "info"].map(sev =>
                      bySeverity[sev].map(ins => <InsightCard key={ins.id} insight={ins} />)
                    )}

                    <VuiBox mt={2} sx={{ background: "rgba(78,205,196,0.06)", border: "1px solid rgba(78,205,196,0.15)", borderRadius: 2, p: "10px 12px" }}>
                      <VuiTypography sx={{ color: "rgba(78,205,196,0.8)", fontSize: 12 }}>
                        Source: rule engine — {insights[0]?.source === "rule_engine" ? "deterministic analysis, no AI hallucination risk" : insights[0]?.source}
                      </VuiTypography>
                    </VuiBox>
                  </>
                )}
              </VuiBox>
            </Card>
          </Grid>

          {/* Narrative panel */}
          <Grid item xs={12} lg={7}>
            <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
              <VuiBox p={3}>
                <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <VuiTypography sx={{ color: TC.primary, fontSize: 15, fontWeight: 700 }}>
                    Management Commentary
                  </VuiTypography>
                  {!narrative && (
                    <VuiButton size="small" color="info" variant="contained"
                      startIcon={<IoSparkles size={14} />}
                      onClick={handleGenerateNarrative} disabled={narLoading || !selectedClient}
                      sx={{ fontSize: 12 }}>
                      {narLoading ? "Generating…" : "Generate Draft"}
                    </VuiButton>
                  )}
                  {narrative && narrative.status !== "approved" && (
                    <VuiButton size="small" color="info" variant="outlined"
                      onClick={handleGenerateNarrative} disabled={narLoading}
                      sx={{ fontSize: 11 }}>
                      {narLoading ? "Regenerating…" : "Regenerate"}
                    </VuiButton>
                  )}
                </VuiBox>

                {loading ? (
                  <LoadingSpinner size="sm" label="Loading…" />
                ) : !narrative ? (
                  <VuiBox py={3} textAlign="center">
                    <VuiTypography sx={{ color: TC.secondary, fontSize: 14, mb: 1 }}>
                      No narrative for {selectedPeriod} yet.
                    </VuiTypography>
                    <VuiTypography sx={{ color: TC.secondary, fontSize: 13, mb: 2 }}>
                      Generate insights first, then draft the management commentary.
                    </VuiTypography>
                    <VuiButton color="info" variant="outlined" size="small"
                      startIcon={<IoSparkles size={14} />}
                      onClick={handleGenerateNarrative} disabled={narLoading || !hasInsights || !selectedClient}>
                      {narLoading ? "Generating…" : "Generate Commentary"}
                    </VuiButton>
                  </VuiBox>
                ) : (
                  <NarrativeCard narrative={narrative} onApprove={openApprove} />
                )}
              </VuiBox>
            </Card>
          </Grid>
        </Grid>

        {/* Approve / Edit Dialog */}
        <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="md" fullWidth
          aria-labelledby="advisory-approve-dialog-title"
          aria-describedby="advisory-approve-dialog-description"
          PaperProps={{ sx: { background: "rgba(15,18,59,0.98)", border: "1px solid #d8e6f7", borderRadius: "16px", color: TC.primary } }}>
          <DialogTitle id="advisory-approve-dialog-title" sx={{ color: TC.primary }}>
            {editMode ? "Edit & Approve Commentary" : "Approve Commentary"}
          </DialogTitle>
          <DialogContent id="advisory-approve-dialog-description">
            {editMode ? (
              <TextField
                multiline fullWidth minRows={10}
                value={editedText}
                onChange={e => setEditedText(e.target.value)}
                sx={{
                  mt: 1,
                  "& .MuiOutlinedInput-root": { color: TC.primary, background: "#f4f8ff", "& fieldset": { bordercolor: "#7a94b0" }, "&:hover fieldset": { bordercolor: "#4a6080" } },
                }}
              />
            ) : (
              <VuiBox sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: 2, p: "14px 16px", mt: 1 }}>
                <VuiTypography sx={{ color: TC.midtone, fontSize: 13, lineHeight: 1.8, whiteSpace: "pre-line" }}>
                  {editedText}
                </VuiTypography>
              </VuiBox>
            )}
            <VuiBox mt={1.5} sx={{ background: "rgba(247,183,49,0.06)", border: "1px solid rgba(247,183,49,0.2)", borderRadius: 1.5, p: "8px 12px" }}>
              <VuiTypography sx={{ color: "#F7B731", fontSize: 12 }}>
                By approving, you confirm this commentary is accurate and suitable for client use.
              </VuiTypography>
            </VuiBox>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <VuiButton variant="outlined" color="white" onClick={() => setApproveOpen(false)} sx={{ fontSize: 12 }}>Cancel</VuiButton>
            <VuiButton variant="contained" color="success" onClick={handleApprove} disabled={approving} sx={{ fontSize: 12, fontWeight: 700 }}>
              {approving ? "Approving…" : "Approve ✓"}
            </VuiButton>
          </DialogActions>
        </Dialog>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

